import 'js-cloudimage-360-view'

import { useStore } from 'effector-react'
import { createMediaStyles } from 'lib/styled-helpers'
import { useResizeObserver } from 'lib/use-resize-observer'
import React, { FC, useEffect, useRef, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { Icon } from 'ui/icon'

import { VideosPreview } from '../../features/car-details-card/components/Preview'
import { $activeTabMobileInDL, setActiveTabMobileInDL } from '../../features/dealer-locator/model'
import { PositionInTab } from '../../features/routing-panel/types'
import { getProjectName } from '../../lib/get-project-name'
import { EcomIconsId } from '../icon/types'
import { Tab } from './Tab'

type Props = {
  value?: number
  customClass?: boolean
  observeActiveTabInDlStatus?: boolean
  arrName?: EcomIconsId[]
  carData?: any
  setSlide?: (index: number) => void
  slickSliderVideosRef?: any
  containerWrapperRef?: any
  tabsLength?: number
  positionImageInTabs?: PositionInTab
  cdnUrl?: string
}

type TabType = {
  content: React.ReactNode
  title: string
  index: number
  iconName?: EcomIconsId
  tabsLength?: number
  image?: string
}

export const Tabs: FC<Props> = ({
  children,
  value,
  customClass = false,
  observeActiveTabInDlStatus,
  arrName,
  carData,
  setSlide,
  slickSliderVideosRef,
  containerWrapperRef,
  tabsLength,
  positionImageInTabs,
  cdnUrl
}) => {
  const [internalTabs, setTabs] = useState<TabType[]>([])
  const [activeTab, setActiveTab] = useState(0)
  const tabListRef = useRef<HTMLDivElement>(null)
  const [activeTabRef, setActiveTabRef] = useState<null | HTMLDivElement>(null)
  const [showArrows, setShowArrows] = useState(false)
  const [navDashWidth, setNavDashWidth] = useState(0)
  const [navDashTranslation, setNavDashTranslation] = useState(0)
  const [activeVideo, setActiveVideo] = useState<null | number>(null)
  const [heightContainer, setHeightContainer] = useState(null)
  const [width, setWidth] = useState(window.innerWidth)
  const activeTabMobileInDL = useStore($activeTabMobileInDL)

  let videos
  if (carData) {
    videos = carData.videos
  }

  useEffect(() => {
    value && setActiveTab(value)
  }, [value])

  useEffect(() => {
    if (!window.CI360 && getProjectName() !== 'haval') return
    window.CI360.update(null, true)
  }, [activeTab])

  useEffect(() => {
    if (activeTabRef && tabListRef.current) {
      const { x: activeTabX, width: activeTabWidth } = activeTabRef.getBoundingClientRect()
      const { x: tabListX, width: tabListWidth } = tabListRef.current.getBoundingClientRect()

      setNavDashWidth(activeTabWidth)
      setNavDashTranslation(tabListRef.current.scrollLeft + activeTabX - tabListX)

      const rightOverflow = activeTabX + activeTabWidth - tabListX - tabListWidth
      const leftOverflow = tabListX - activeTabX

      if (rightOverflow > 0) {
        tabListRef.current.scrollBy({ left: rightOverflow })
      } else if (leftOverflow > 0) {
        tabListRef.current.scrollBy({ left: -leftOverflow })
      }
    }
  }, [activeTabRef])

  useEffect(() => {
    let internalTabs: TabType[] = []
    React.Children.map(children, (child, index) => {
      if (React.isValidElement(child)) {
        internalTabs = [
          ...internalTabs,
          {
            content: child.props.children,
            image: child.props.image,
            title: child.props.title,
            index,
            iconName: arrName && arrName[index]
          }
        ]
      }
    })
    setTabs(internalTabs)
  }, [children])

  useResizeObserver(tabListRef, () => {
    if (!tabListRef.current) return

    if (tabListRef.current.clientWidth < tabListRef.current.scrollWidth) {
      setShowArrows(true)
    } else {
      setShowArrows(false)
    }
  })

  useEffect(() => {
    const handler = () => {
      setWidth(window.innerWidth)
    }
    setTimeout(() => {
      containerWrapperRef?.current.clientHeight && setHeightContainer(containerWrapperRef?.current.clientHeight)
    }, 0)
    window.addEventListener('resize', handler)

    return () => window.removeEventListener('resize', handler)
  }, [width])

  useEffect(() => {
    if (!observeActiveTabInDlStatus) return
    setActiveTab(activeTabMobileInDL)
  }, [activeTabMobileInDL])

  return (
    <>
      <TabListWrapper customClass={customClass}>
        <TabList ref={tabListRef} tabLength={tabsLength}>
          <TabListContent>
            {internalTabs.length > 1 &&
              internalTabs.map((tab, index) => {
                return (
                  <TabWrapper
                    ref={(el) => {
                      if (el && tab.index === activeTab) {
                        setActiveTabRef(el)
                      }
                    }}
                    key={index}
                  >
                    <Tab
                      title={tab.title}
                      onClick={() => {
                        setActiveTabMobileInDL(0)
                        setActiveTab(tab.index)
                      }}
                      active={tab.index === activeTab}
                      iconName={tab.iconName}
                      customClass={customClass}
                    >
                      <Block position={positionImageInTabs as PositionInTab}>
                        {positionImageInTabs !== 'instead_of_text' && <TabText>{tab.title}</TabText>}
                        {tab.image && positionImageInTabs && (
                          <ImageWrap position={positionImageInTabs as PositionInTab}>
                            <img src={cdnUrl + '/image/' + tab.image} alt="img" />
                          </ImageWrap>
                        )}
                      </Block>
                    </Tab>
                  </TabWrapper>
                )
              })}
            {videos && videos.length > 0 && getProjectName() === 'haval' && (
              <VideoTab onClick={() => setActiveVideo(0)}>
                <Icon name={'video-grey'} size={1.5} />
              </VideoTab>
            )}
          </TabListContent>
          <NavDash style={{ width: navDashWidth, transform: `translateX(${navDashTranslation}px)` }} />
        </TabList>

        {showArrows && (
          <>
            <ArrowPrev
              onClick={() => activeTab > 0 && setActiveTab((activeTab) => activeTab - 1)}
              active={activeTab !== 0}
            >
              <Icon name="arrow-left" size={0.75} />
            </ArrowPrev>
            <ArrowNext
              onClick={() => activeTab < internalTabs.length - 1 && setActiveTab((activeTab) => activeTab + 1)}
              active={activeTab !== internalTabs.length - 1}
            >
              <Icon name="arrow-right" size={0.75} />
            </ArrowNext>
          </>
        )}
      </TabListWrapper>
      <ContainerWrapper heightContainer={heightContainer}>
        <Container index={activeTab} ref={containerWrapperRef}>
          {internalTabs.map((tab, index) => {
            return (
              <TabPanel active={tab.index === activeTab} key={index}>
                {tab.content}
              </TabPanel>
            )
          })}
        </Container>
      </ContainerWrapper>

      {activeVideo !== null && videos && (
        <VideosPreview
          activeSlide={activeVideo}
          onSlideChange={setActiveVideo}
          videos={videos}
          setSlide={setSlide}
          slickSliderVideosRef={slickSliderVideosRef}
        />
      )}
    </>
  )
}

const TabList = styled.div<{ tabLength?: number }>`
  overflow: auto;
  border-bottom: ${({ theme, tabLength }) => {
    if (getProjectName() === 'generic_v2') return `1px solid ${theme.palette.grey[900]}`
    return getProjectName() !== 'haval' && tabLength && '1px solid #d3d3d3'
  }};
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    display: none;
  }

  & {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`

const TabListWrapper = styled.div<{ customClass: boolean }>`
  position: relative;

  position: ${(customClass) => customClass.customClass && 'absolute'};
  z-index: ${(customClass) => customClass.customClass && 10};
  top: ${(customClass) => customClass.customClass && 'calc(100% + 10px)'};
  right: ${(customClass) => customClass.customClass && 0};
`

const TabListContent = styled.div`
  display: flex;
  padding-bottom: 1px;
  align-items: center;
  transition: transform 0.25s;
`

const TabWrapper = styled.div``

const TabText = styled.p`
  margin-top: 0;
  margin-bottom: 0;
  ${({ theme }) => theme.textStyles.p2};
  color: ${({ theme }) => {
    if (getProjectName() === 'tank') return theme.palette.grey[500]
    if (getProjectName() === 'generic_v2') return
    return theme.palette.grey[900]
  }};
  font-weight: ${({ theme }) => {
    if (getProjectName() === 'generic_v2') return 400
    if (getProjectName() === 'haval') return 500
    return 700
  }};
  text-transform: uppercase;
  font-size: ${({ theme }) => {
    if (getProjectName() === 'generic_v2') return '20px'
    return '16px'
  }};

  @media screen and (max-width: 768px) {
    font-size: 16px;
  }
`

const ImageWrap = styled.div<{ position: PositionInTab }>`
  width: 35px;
  height: 35px;
  display: flex;
  justify-content: center;
  align-items: center;
  img {
    width: 80%;
    height: 80%;
  }

  margin: ${({ position }) => {
    if (position === 'before_text') return '0 5px 0 0'
    if (position === 'after_text') return '0 0 0 5px'
    return '0'
  }};
`

const Block = styled.div<{ position: PositionInTab }>`
  display: flex;
  align-items: center;
  flex-direction: ${({ position }) => {
    if (position === 'before_text') return 'row-reverse'
    return 'row'
  }};
`

const Arrow = styled.button<{ active: boolean }>`
  position: absolute;
  top: 50%;
  padding: 0;
  background: none;
  border: none;
  transform: translateY(-50%);
  cursor: pointer;
  opacity: ${({ active }) => (active ? '1' : '0.5')};
  outline: none;
  color: ${({ theme }) => {
    if (getProjectName() === 'tank') return theme.palette.grey[500]
    if (getProjectName() === 'generic_v2') return theme.palette.grey[100]
    return
  }};

  ${createMediaStyles(() => ({
    phone: `display: none;`,
    tablet: `display: block;`
  }))};
`

const ArrowPrev = styled(Arrow)`
  left: ${({ theme }) => `-${theme.spacing[5]}`};
`

const ArrowNext = styled(Arrow)`
  right: ${({ theme }) => `-${theme.spacing[5]}`};
`

const ContainerWrapper = styled.div<{ heightContainer: number | null }>`
  width: 100%;
  height: ${({ heightContainer }) => {
    return heightContainer + 'px'
  }};
`

const Container = styled.div<{ index: number }>`
  max-width: 100%;
  height: 100%;
`

const TabPanel = styled.div<{ active: boolean }>`
  display: ${(props) => (props.active ? 'initial' : 'none')};
  min-width: 100%;
  height: 100%;
`

const NavDash = styled.div`
  background: ${({ theme }) => {
    if (getProjectName() === 'generic_v2') return theme.palette.grey[100]
    if (getProjectName() === 'avito') return '#222222'
    return theme.palette.brand.primary
  }};
  height: 2px;
  transition: transform 0.25s, width 0.25s;
  transition-delay: 0.1s;
`

const VideoTab = styled.div`
  margin-left: 10px;
  cursor: pointer;
  opacity: 0.3;
  transition: 0.2s linear;
  &:hover {
    opacity: 0.5;
  }
`
